/* @flow */

import React from 'react';
import Immutable from 'immutable';
import mapboxgl from 'mapbox-gl';
import debounce from 'lodash/debounce';
import PropTypes from '../util/prop_types';
import AttributionControl from './attribution_control';

export default class MapGL extends React.PureComponent {
  constructor(props) {
    super(props);

    if (props.accessToken) {
      mapboxgl.accessToken = props.accessToken;
    }

    this.state = {
      // Whether the cursor is down
      isDragging: false,
      // Whether the cursor is over a clickable feature
      isHovering: false,

      map: undefined,
      ready: false
    };
  }

  static propTypes = {
    accessToken: PropTypes.string,

    //Settings
    preserveDrawingBuffer: PropTypes.bool, /** Mapbox WebGL context creation option. Useful when you want to export the canvas as a PNG. */
    attributionControl: PropTypes.bool,
    attributions: PropTypes.array,
    scrollZoom: PropTypes.bool,
    dragPan: PropTypes.bool,
    dragRotate: PropTypes.bool,
    doubleClickZoom: PropTypes.bool,
    touchZoomRotate: PropTypes.bool,
    preventStyleDiffing: PropTypes.bool, /** There are known issues with style diffing. As stopgap, add option to prevent style diffing. */
    visible: PropTypes.bool,
    clickRadius: PropTypes.number,
    movingMethod: PropTypes.oneOf(['jumpTo', 'easeTo', 'flyTo']),

    /** The Mapbox style. A string url or a MapboxGL style Immutable.Map object. */
    mapStyle: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Immutable.Map)
    ]).isRequired,

    //viewport
    width: PropTypes.number,
    height: PropTypes.number,
    center: PropTypes.array,
    zoom: PropTypes.arrayOf(PropTypes.number).isRequired,
    bearing: PropTypes.number,
    pitch: PropTypes.number,
    maxZoom: PropTypes.number,
    minZoom: PropTypes.number,
    maxPitch: PropTypes.number,
    minPitch: PropTypes.number,
    maxBounds: PropTypes.array,

    //Callbacks
    onLoad: PropTypes.func,
    onViewportChange: PropTypes.func,
    onFeatureClick: PropTypes.func,
    getHoverCursor: PropTypes.func,
  }

  static defaultProps = {
    preserveDrawingBuffer: false,
    attributionControl: true,
    preventStyleDiffing: false,
    visible: true,
    clickRadius: 0,
    movingMethod: 'flyTo',

    center: [-120.66108612454394, 35.30112781592855],
    zoom: [11],
    bearing: 0,
    pitch: 0,
    minZoom: 0,
    maxZoom: 20,
    minPitch: 0,
    maxPitch: 60,
  };

  static childContextTypes = {
    map: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
  }

  getChildContext() {
    return {
      map: this.state.map,
      width: this.props.width,
      height: this.props.height,
    };
  }

  componentDidMount() {
    this._isMounted = true;

    const mapStyle = Immutable.Map.isMap(this.props.mapStyle) ?
      this.props.mapStyle.toJS() :
      this.props.mapStyle;

    const map = new mapboxgl.Map({
      container: this.container,
      center: this.props.center,
      zoom: this.props.zoom[0],
      pitch: this.props.pitch,
      bearing: this.props.bearing,
      style: mapStyle,
      attributionControl: this.props.attributionControl,
      preserveDrawingBuffer: this.props.preserveDrawingBuffer,
      maxBounds: this.props.maxBounds,
    });

    this.setState({ map });

    //add controls
    map.addControl(new AttributionControl({
        attributions: this.props.attributions
    }));
    map.addControl(new mapboxgl.ScaleControl({
      maxWidth: 80,
      unit: 'imperial'
    }), 'bottom-right');
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    map.addControl(new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      }
    }), 'bottom-right');

    // Disable outline style
    const canvas = map.getCanvas();
    if (canvas) {
      canvas.style.outline = 'none';
    }

    // Attach events
    map.on('load', (evt: React.SyntheticEvent<any>) => {
      if (this._isMounted) {
        this.setState({ ready: true });
      }
    });

    if (this.props.onLoad) {
      map.once('load', this.props.onLoad);
    }

    if (this.props.onViewportChange) {
      map.on('move', debounce(this._onViewportChange, 50));
    }

    if (this.props.onFeatureClick) {
      map.on('click', this._onFeatureClick);
    }

    if (this.props.getHoverCursor) {
      map.on('mousemove', this._onFeatureHover);
    }

    //map.on('mousemove', this.onMapHover);
    //map.on('click', this.onMapClick);
    /*
    Object.keys(events).forEach((event, index) => {
      const propEvent = this.props[event];

      if (propEvent) {
        map.on(events[event], (evt: React.SyntheticEvent<any>) => {
          propEvent(map, evt);
        });
      }
    });
    */
  }

  componentWillReceiveProps(newProps) {
    if (newProps.accessToken && newProps.accessToken != this.props.accessToken) {
      mapboxgl.accessToken = newProps.accessToken;
    }

    this._updateMapViewport(this.props, newProps);
    this._updateMapStyle(this.props, newProps);
    // Save width/height so that we can check them in componentDidUpdate
    this.setState({
      width: this.props.width,
      height: this.props.height
    });
  }

  componentWillUpdate(nextProps) {
  }

  componentDidUpdate() {
    // Since Mapbox's map.resize() reads size from DOM
    // we must wait to read size until after render (i.e. here in "didUpdate")
    this._updateMapSize(this.state, this.props);
  }

  componentWillUnmount() {
    const { map } = this.state;
    this._isMounted = false;

    if (map) {
      map.remove();
    }
  }

  // External apps can access map this way
  getMap = () => {
    return this.state.map;
  }

  _onViewportChange = () => {
    const { map } = this.state;

    this.props.onViewportChange({
      center: map.getCenter().toArray(),
      zoom: [map.getZoom()],
      bearing: map.getBearing(),
      pitch: map.getPitch()
    });
  }

  _onFeatureClick = (event) => {
    event.features = this._getFeatures(event.point, this.props.clickRadius);
    if (event.features.length > 0) {
      this.props.onFeatureClick(event);
    }
  }

  _onFeatureHover = (event) => {
    const { map } = this.state;
    event.features = this._getFeatures(event.point, this.props.clickRadius);
    const cursor = this.props.getHoverCursor(event);
    map.getCanvas().style.cursor = cursor;
  }

  _getFeatures = (point, radius) => {
    let features;
    if (radius) {
      // Radius enables point features, like marker symbols, to be clicked.
      const bbox = [
        [point.x - radius, point.y - radius],
        [point.x + radius, point.y + radius]
      ];
      features = this.state.map.queryRenderedFeatures(bbox);
    } else {
      features = this.state.map.queryRenderedFeatures(point);
    }
    return features;
  }

  _updateMapStyle = (oldProps, newProps) => {
    const { map } = this.state;
    if (!map) {
      return null;
    }

    const newMapStyle = newProps.mapStyle;
    const oldMapStyle = oldProps.mapStyle;
    if (newMapStyle !== oldMapStyle) {
      if (Immutable.Map.isMap(newMapStyle) && Immutable.Map.isMap(oldMapStyle)) {
        if(newMapStyle.equals(oldMapStyle)) {
          map.setStyle(newMapStyle.toJS());
        }
      } else {
        map.setStyle(newMapStyle);
      }
    }
  }

  _updateMapViewport = (oldProps, newProps) => {
    const { map } = this.state;
    if (!map) {
      return null;
    }

    const center = map.getCenter();
    const zoom = map.getZoom();
    const bearing = map.getBearing();
    const pitch = map.getPitch();

    const didZoomUpdate = oldProps.zoom !== newProps.zoom
      && (newProps.zoom && newProps.zoom[0]) !== zoom;

    const didCenterUpdate = oldProps.center !== newProps.center &&
      ((newProps.center && newProps.center[0]) !== center.lng ||
        (newProps.center && newProps.center[1]) !== center.lat);

    const didBearingUpdate = oldProps.bearing !== newProps.bearing
      && newProps.bearing !== bearing;

    const didPitchUpdate = oldProps.pitch !== newProps.pitch
      && newProps.pitch !== pitch;

    if (newProps.maxBounds && oldProps.maxBounds !== newProps.maxBounds) {
      map.setMaxBounds(newProps.maxBounds);
    }

    if (newProps.fitBounds) {
      const didFitBoundsUpdate =
        oldProps.fitBounds !== newProps.fitBounds || // Check for reference equality
        newProps.fitBounds.length !== (oldProps.fitBounds && fitBounds.length) || // Added element
        !!newProps.fitBounds.filter((c, i) => {
          // Check for equality
          const nc = newProps.fitBounds && newProps.fitBounds[i];
          return c[0] !== (nc && nc[0]) || c[1] !== (nc && nc[1]);
        })[0];

      if (didFitBoundsUpdate) {
        map.fitBounds(newProps.fitBounds, newProps.fitBoundsOptions);
      }
    }

    if (
      didZoomUpdate ||
      didCenterUpdate ||
      didBearingUpdate ||
      didPitchUpdate
    ) {
      const movingMethod = newProps.movingMethod || defaultMovingMethod;
      const { flyToOptions, animationOptions } = newProps;

      map[movingMethod]({
        ...animationOptions,
        ...flyToOptions,
        zoom: didZoomUpdate && newProps.zoom ? newProps.zoom[0] : zoom,
        center: didCenterUpdate ? newProps.center : center,
        bearing: didBearingUpdate ? newProps.bearing : bearing,
        pitch: didPitchUpdate ? newProps.pitch : pitch
      });
    }
  }

  _updateMapSize = (oldProps, newProps) => {
    const sizeChanged =
      oldProps.width !== newProps.width || oldProps.height !== newProps.height;

    if (sizeChanged) {
      this.state.map.resize();
    }
  }

  setRef = (ref) => {
    this.container = ref;
  }

  render(): ?React.Element<any> {
    const {className, width, height, style, visible} = this.props;
    const { ready } = this.state;

    const mapContainerStyle = Object.assign({}, style, {width, height});

    const mapStyle = Object.assign({}, style, {
      width,
      height,
      visibility: visible ? 'visible' : 'hidden'
    });

    const overlayContainerStyle = {
      position: 'absolute',
      left: 0,
      top: 0,
      width,
      height,
      overflow: 'hidden',
      pointerEvents: 'none'
    };

    const eventCanvasStyle = {
      width,
      height,
    };

    return (
      <div
        key='map-container'
        style={mapContainerStyle} >
        <div
          id="map"
          key='map-mapbox'
          ref={this.setRef}
          style={mapStyle}
          className={className} >
        </div>
        <div
          key='map-overlays'
          className='overlays'
          style={overlayContainerStyle} >
          {ready && this.props.children}
        </div>
      </div>
    );
  }
}
